// Warblue pair builder: frame (Crystal / Black) + lens -> pairs. See snippets/warblue-pair-builder.liquid.
// State: quantities by key "c:G" (frame:lens). Max 4 pairs. 2 pairs = pay for 1, 3+ = 50% off (the automatic discounts do the real
// math in the cart; here it is only displayed). An exact bundle (same-model pair, Duo, Crystal 3/4, black packs) adds that bundle product.
const MAX = 99; // no limit of pairs
const total = (q) => Object.values(q).reduce((s, n) => s + n, 0);

export function formatMoney(cents, currency, locale) {
  try { return new Intl.NumberFormat(locale || 'en', { style: 'currency', currency }).format(cents / 100); } catch { return (cents / 100).toFixed(2); }
}

const Base = typeof HTMLElement !== 'undefined' ? HTMLElement : class {};

class WarbluePairBuilder extends Base {
  connectedCallback() {
    { const pi = this.closest('.product-info'), bi = this.closest('.product-info__block-item'); if (pi) pi.classList.add('wb-hv2', 'wb-hv2wpb'); if (bi) bi.classList.add('wb-hv2'); }
    this.currency = this.dataset.currency || 'EUR';
    this.locale = document.documentElement.lang || 'en';
    this.view = this.dataset.view === 'b' ? 'b' : 'c';
    this.pageKey = this.dataset.pageKey || '';
    this.focus = this.dataset.focus || '';
    this.cards = Array.from(this.querySelectorAll('[data-wpb-card]'));
    this.byKey = {};
    this.cards.forEach((c) => { this.byKey[c.dataset.key] = c; });
    // bundle products that exist: set key -> handle; price/variant are fetched only for the selection that matches
    this.packHandle = {};
    (this.dataset.packs || '').split(',').forEach((p) => { const i = p.indexOf('='); if (i > 0) this.packHandle[p.slice(0, i)] = p.slice(i + 1); });
    this.packs = {};     // handle -> {variantId, price, compare} | false (not available)
    this.packLoading = {};

    // blocks text ("Blocks {pct}")
    this.querySelectorAll('.wbb__v2-blocks[data-pct]').forEach((el) => { el.textContent = el.textContent.replace('{pct}', el.dataset.pct); });

    // initial selection: "b:N,c:N" or the page's own product; keys without a product (draft) are dropped
    this.q = {};
    const wanted = (this.dataset.initial || this.pageKey || '').split(',').map((s) => s.trim()).filter(Boolean);
    wanted.forEach((k) => { if (this.byKey[k]) this.q[k] = (this.q[k] || 0) + 1; });
    if (!total(this.q)) {
      const first = this.cards.find((c) => c.dataset.available !== 'false' && c.dataset.frame === this.view) || this.cards[0];
      if (first) this.q[first.dataset.key] = 1;
    }

    // the page's own lens (or the lenses of its pack) come first in every row of cards, then the rest in the usual order
    {
      const ORD = ['D', 'N', 'G', 'E', 'L', 'A'];
      const mine = new Set(Object.keys(this.q).map((k) => k.split(':')[1]));
      const rank = (c) => (mine.has(c) ? 0 : 10) + ORD.indexOf(c);
      this.querySelectorAll('[data-wpb-grid]').forEach((g) => {
        g.querySelectorAll('[data-wpb-card]').forEach((c) => { c.style.order = String(rank(c.dataset.code)); });
        const nb = g.querySelector('[data-wpb-next]'); if (nb) nb.style.order = '100';
      });
    }

    // the pre-launch countdown sits above the product title, not inside the selector
    {
      const cd = this.querySelector('[data-wpb-countdown]');
      const titleBlock = document.querySelector('.product-info__block-item[data-block-type="title"]');
      if (cd && titleBlock && titleBlock.parentNode) { cd.style.margin = '0 0 14px'; cd.style.order = ''; titleBlock.parentNode.insertBefore(cd, titleBlock); }
    }

    // frame tiles show the focus lens (or Da Vinci) in each frame
    const lensForTiles = this.focus || (this.pageKey ? this.pageKey.split(':')[1] : 'D');
    this.querySelectorAll('[data-wpb-frame-img]').forEach((img) => {
      const c = this.byKey[img.dataset.wpbFrameImg + ':' + lensForTiles] || this.cards.find((x) => x.dataset.frame === img.dataset.wpbFrameImg);
      if (c) img.src = c.dataset.image + (c.dataset.image.includes('?') ? '&' : '?') + 'width=400'; else img.closest('button').hidden = true;
    });
    this.lastLens = this.focus || (this.pageKey ? this.pageKey.split(':')[1] : '');

    this.galleryHandle = this.dataset.pageHandle || '';

    // (frame tiles replaced by the swap button)
    this.querySelectorAll('[data-wpb-frame]').forEach((btn) => { btn.addEventListener('click', () => this.setView(btn.dataset.wpbFrame)); }); // wpbFrameBtn
    const swap = this.querySelector('[data-wpb-swap]');
    if (swap) swap.addEventListener('click', () => this.setView(this.view === 'c' ? 'b' : 'c'));
    this.querySelectorAll('[data-wpb-grid]').forEach((gridEl) => {
      const nextBtn = gridEl.querySelector('[data-wpb-next]') || (gridEl.parentElement && gridEl.parentElement.querySelector(':scope > [data-wpb-next]'));
      if (!nextBtn) return;
      nextBtn.addEventListener('click', () => { const c = gridEl.querySelector('[data-wpb-card]'); gridEl.scrollBy({ left: (c ? c.getBoundingClientRect().width + 6 : 100) * 2, behavior: 'smooth' }); });
      const upd = () => { nextBtn.hidden = gridEl.scrollLeft + gridEl.clientWidth >= gridEl.scrollWidth - 6; };
      gridEl.addEventListener('scroll', upd, { passive: true }); setTimeout(upd, 80); window.addEventListener('resize', upd);
      // the round arrow sits outside the card edge, centred on the white part of the cards (the black pre-order bar does not count)
      const placeArrow = () => {
        const wrap = gridEl.parentElement; if (!wrap || !wrap.classList.contains('wpb__gridwrap')) return;
        const bar = gridEl.querySelector('.wpb__prebar'); const barH = bar ? bar.offsetHeight : 0;
        nextBtn.style.top = Math.max(0, (gridEl.offsetHeight - barH) / 2 - nextBtn.offsetHeight / 2) + 'px';
      };
      setTimeout(placeArrow, 120); setTimeout(placeArrow, 700); window.addEventListener('resize', placeArrow); if (document.fonts && document.fonts.ready) document.fonts.ready.then(placeArrow);
    });
    const more = this.querySelector('[data-wpb-more]'), moreBtn = this.querySelector('[data-wpb-more-btn]');
    const setMore = (open) => { if (!more || !moreBtn) return; more.classList.toggle('is-open', open); moreBtn.setAttribute('aria-expanded', String(open)); moreBtn.textContent = open ? moreBtn.dataset.tLess : moreBtn.dataset.tMore; };
    if (moreBtn) moreBtn.addEventListener('click', () => setMore(!more.classList.contains('is-open')));
    // a selection that already has pairs of the other frame (a Duo) opens it from the start
    this.openMore = () => setMore(true);
    if (Object.keys(this.q).some((k) => k[0] !== (this.dataset.view || 'c') && this.q[k] > 0)) setMore(true);
    // lens cards
    this.cards.forEach((card) => {
      const key = card.dataset.key;
      card.addEventListener('click', (e) => {
        if (e.target.closest('[data-wbb-decrease], [data-wbb-increase]')) return;
        if (card.dataset.available === 'false') return;
        const n = this.q[key] || 0;
        this.lastLens = card.dataset.code;
        this.detailUntil = Date.now() + 3000; clearTimeout(this.detailTimer); this.detailTimer = setTimeout(() => this.render(), 3050);
        if (n === 0) this.add(key); else if (total(this.q) > 1) this.remove(key, true); else this.render();
      });
      card.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); card.click(); } });
      card.querySelector('[data-wbb-decrease]').addEventListener('click', (e) => { e.stopPropagation(); this.remove(key, false); });
      card.querySelector('[data-wbb-increase]').addEventListener('click', (e) => { e.stopPropagation(); this.add(key); });
    });
    const another = this.querySelector('[data-wbb-v2-another]');
    if (another) another.addEventListener('click', () => {
      const k = (this.lastKey && this.q[this.lastKey]) ? this.lastKey : this.list()[0];
      if (k) { this.lastLens = k.split(':')[1]; this.detailUntil = Date.now() + 3000; clearTimeout(this.detailTimer); this.detailTimer = setTimeout(() => this.render(), 3050); this.add(k); }
    });

    // compare link label (the lens popup trigger is injected by another section)
    const label = this.dataset.tCompare || '';
    const apply = () => document.querySelectorAll('.wlpe-trigger-text').forEach((el) => el.setAttribute('data-wbb-compare', label));
    apply();
    this.mo = new MutationObserver(apply);
    this.mo.observe(document.body, { childList: true, subtree: true });
    setTimeout(() => this.mo && this.mo.disconnect(), 8000);

    // add to cart: intercept in capture phase, resolved fresh from the clicked button
    this.submitting = false;
    this.onDocClick = (event) => {
      const btn = event.target.closest('button[type="submit"]');
      if (!btn || !this.isConnected) return;
      const form = btn.closest('form');
      if (!form || !form.action || !form.action.includes('/cart/add')) return;
      let scope = this.parentElement;
      while (scope && !scope.querySelector('.product-info__buy-buttons') && scope !== document.body) scope = scope.parentElement;
      if (scope && scope !== document.body && !scope.contains(btn)) return;
      // exactly the page's own product, one pair: let the theme add it natively
      if (this.isNativeSelection()) return;
      event.preventDefault(); event.stopPropagation(); event.stopImmediatePropagation();
      this.submit(form);
    };
    document.addEventListener('click', this.onDocClick, true);

    this.render();
  }

  disconnectedCallback() {
    if (this.mo) this.mo.disconnect();
    if (this.onDocClick) document.removeEventListener('click', this.onDocClick, true);
  }

  tickCountdown() {
    const el = this.cdEl;
    if (!el || el.hidden) return;
    const end = new Date(el.dataset.end);
    const paint = () => {
      const sec = Math.floor((end - new Date()) / 1000);
      if (isNaN(end.getTime()) || sec <= 0) { el.hidden = true; return; }
      const d = Math.floor(sec / 86400), r = sec % 86400, p = (n) => (n < 10 ? '0' : '') + n;
      el.innerHTML = '<span></span><b></b>';
      el.firstChild.textContent = el.dataset.label || '';
      el.lastChild.textContent = (d > 0 ? d + 'd ' : '') + p(Math.floor(r / 3600)) + ':' + p(Math.floor((r % 3600) / 60)) + ':' + p(r % 60);
    };
    paint();
    if (!this.cdTimer) this.cdTimer = setInterval(() => { if (!this.isConnected) { clearInterval(this.cdTimer); return; } paint(); }, 1000);
  }

  // the live stock line and the "Expected delivery" line: while a pre-order pair is selected they read
  // "Pre-order · Limited units" and "Ships by <date>" (same design, only the words change); otherwise they are restored
  liveBlocks(anyPre) {
    this.preActive = anyPre;
    if (!this.liveObs) {
      // other scripts (stock counter, delivery estimate) re-render their lines: keep the pre-order wording on top of them
      this.liveObs = new MutationObserver(() => { if (this.isConnected && this.preActive) this.liveBlocks(true); });
      const scope0 = document.querySelector('.product-info__buy-buttons') ? document.querySelector('.product-info__buy-buttons').closest('product-info, .product-info') || document.body : document.body;
      scope0.querySelectorAll('[data-inventory-text], warblue-estimated-shipping').forEach((el) => this.liveObs.observe(el, { childList: true, subtree: true, characterData: true }));
    }
    let scope = this.parentElement;
    while (scope && !scope.querySelector('.product-info__buy-buttons') && scope !== document.body) scope = scope.parentElement;
    if (!scope) return;
    const label = scope.querySelector('[data-inventory-text] .wib-label');
    const stockRoot = scope.querySelector('[data-inventory-text]');
    if (stockRoot) { if (anyPre) { if (stockRoot.hasAttribute('data-ready')) { stockRoot.dataset.wasReady = '1'; stockRoot.removeAttribute('data-ready'); } } else if (stockRoot.dataset.wasReady) { stockRoot.setAttribute('data-ready', '1'); } }
    const wes = scope.querySelector('warblue-estimated-shipping');
    if (label) {
      if (anyPre) { if (this.origStock === undefined) this.origStock = label.innerHTML; if (label.textContent !== (this.dataset.tPreStock || '')) label.textContent = this.dataset.tPreStock || ''; }
      else if (this.origStock !== undefined) { label.innerHTML = this.origStock; this.origStock = undefined; }
    }
    if (wes) {
      const rows = wes.querySelectorAll('.warblue-estimated-shipping__row');
      const row = rows[rows.length - 1];
      const lab = row && row.querySelector('span'), dt = row && row.querySelector('[data-wes-delivery]');
      if (lab && dt) {
        if (anyPre) {
          if (this.origShip === undefined) this.origShip = { l: lab.textContent, d: dt.textContent };
          let when = '';
          try { when = new Intl.DateTimeFormat(document.documentElement.lang || 'en', { month: 'short', day: 'numeric' }).format(new Date((this.dataset.shipsDate || '2026-10-31') + 'T12:00:00')); } catch { when = this.dataset.shipsDate || ''; }
          const wantL = this.dataset.tShipsLabel || 'Ships by';
          if (lab.textContent !== wantL) lab.textContent = wantL;
          if (dt.textContent !== when) dt.textContent = when;
        } else if (this.origShip !== undefined) { lab.textContent = this.origShip.l; dt.textContent = this.origShip.d; this.origShip = undefined; }
      }
    }
  }

  // the buy button says Pre-order only while a pre-order pair is in the selection, Add to cart otherwise
  applyButtonText(anyPre) {
    this.wantPre = anyPre;
    let scope = this.parentElement;
    while (scope && !scope.querySelector('.product-info__buy-buttons') && scope !== document.body) scope = scope.parentElement;
    const btn = scope && scope.querySelector('.product-info__buy-buttons button[type="submit"]');
    if (!btn) return;
    this.btnEl = btn;
    const apply = () => {
      const box = btn.querySelector('div') || btn;
      const want = this.dataset.tAddCart; // always Add to cart: the pre-order notice is the black pill above
      if (want && box.textContent.trim() !== want) box.textContent = want;
    };
    apply();
    if (!this.btnObs) {
      this.btnObs = new MutationObserver(() => { if (this.isConnected) { this.btnObs.disconnect(); apply(); this.btnObs.observe(this.btnEl, { childList: true, characterData: true, subtree: true }); } });
      this.btnObs.observe(btn, { childList: true, characterData: true, subtree: true });
    }
  }

  setView(f) {
    // the frame circles only change which frame the lens circles add to: pairs already chosen stay as they are,
    // so one Crystal pair and one Black pair can be mixed freely
    this.view = f;
    this.render();
  }

  add(key) {
    if (total(this.q) >= MAX) return;
    this.lastKey = key;
    this.q = { ...this.q, [key]: (this.q[key] || 0) + 1 };
    this.render();
  }
  remove(key, whole) {
    const n = this.q[key] || 0;
    if (!n) return;
    if (total(this.q) <= 1) return; // always at least one pair
    const q = { ...this.q };
    if (whole) delete q[key]; else if (n <= 1) delete q[key]; else q[key] = n - 1;
    if (!total(q)) return;
    this.q = q;
    this.render();
  }

  list() { // sorted list of keys, repeated by quantity
    const out = [];
    Object.keys(this.q).forEach((k) => { for (let i = 0; i < this.q[k]; i++) out.push(k); });
    return out.sort();
  }
  matchedPack() {
    const l = this.list();
    if (l.length < 2) return null;
    const handle = this.packHandle[l.join('+')];
    if (!handle) return null;
    const hit = this.packs[handle];
    if (hit) return hit;
    if (hit === undefined && !this.packLoading[handle]) {
      this.packLoading[handle] = true;
      const root = window.Shopify?.routes?.root || '/';
      fetch(root + 'products/' + handle + '.js').then((r) => (r.ok ? r.json() : null)).then((p) => {
        const v = p && p.available && p.variants && p.variants[0];
        this.packs[handle] = v ? { variantId: v.id, price: v.price, compare: v.compare_at_price > v.price ? v.compare_at_price : null } : false;
      }).catch(() => { this.packs[handle] = false; }).then(() => { this.packLoading[handle] = false; if (this.isConnected) this.render(); });
    }
    return null;
  }
  isNativeSelection() {
    const l = this.list();
    return l.length === 1 && l[0] === this.pageKey;
  }

  render() {
    const n = total(this.q);
    this.querySelectorAll('[data-wpb-frame]').forEach((btn) => { btn.setAttribute('aria-checked', String(btn.dataset.wpbFrame === this.view)); });
    // how many pairs of each frame are in the selection (a mixed bundle shows 1 and 1)
    const mixedFrames = ['c', 'b'].every((f) => Object.keys(this.q).some((k) => k[0] === f && this.q[k] > 0));
    ['c', 'b'].forEach((f) => { const n = Object.keys(this.q).filter((k) => k[0] === f).reduce((a, k) => a + this.q[k], 0); this.querySelectorAll('[data-wpb-fcount="' + f + '"]').forEach((el) => { el.textContent = n && mixedFrames ? String(n) : ''; }); });
    // cards of the current frame only
    this.cards.forEach((card) => {
      const key = card.dataset.key;
      const qty = this.q[key] || 0;
      card.classList.toggle('is-selected', qty > 0);
      card.querySelector('[data-wbb-qty]').classList.toggle('is-active', qty > 0);
      card.querySelector('[data-wbb-qty-value]').textContent = String(qty);
      card.querySelector('[data-wbb-decrease]').disabled = n <= 1 && qty > 0;
      card.querySelector('[data-wbb-increase]').disabled = n >= MAX;
    });

    // swap button: the same glasses in the opposite frame
    const other = this.view === 'c' ? 'b' : 'c';
    const lensNow = this.lastLens || this.focus || (this.list()[0] || '').split(':')[1] || 'D';
    const oc = this.byKey[other + ':' + lensNow] || this.cards.find((c) => c.dataset.frame === other);
    const sw = this.querySelector('[data-wpb-swap]');
    if (sw) {
      sw.hidden = !oc;
      if (oc) {
        const im = sw.querySelector('[data-wpb-swap-img]'); const u = oc.dataset.image + (oc.dataset.image.includes('?') ? '&' : '?') + 'width=400';
        if (im.getAttribute('src') !== u) im.setAttribute('src', u);
        sw.querySelector('[data-wpb-swap-label]').textContent = (this.dataset.tSwap || '').replace('{frame}', other === 'c' ? (this.dataset.tFrameC || '') : (this.dataset.tFrameB || ''));
      }
    }
    if (this.updNext) setTimeout(this.updNext, 0);

    // dynamic values after the labels
    const fname = this.view === 'c' ? this.dataset.tFrameC : this.dataset.tFrameB;
    this.querySelectorAll('[data-wpb-frame-val]').forEach((el) => { el.textContent = fname || ''; });
    this.cards.forEach((c) => { c.dataset.count = String(this.q[c.dataset.key] || 0); });
    const COLORS = (this.dataset.tColors || 'Red,Orange,Yellow,Clear,Light grey,Dark grey').split(',').map((x) => x.trim());
    const ORDER = ['D', 'N', 'G', 'E', 'L', 'A'];
    const codes = ORDER.filter((c) => Object.keys(this.q).some((k) => k.split(':')[1] === c && this.q[k] > 0));
    const detail = Date.now() < (this.detailUntil || 0) || codes.length < 2;
    const shown = this.cards.find((c) => c.dataset.code === (this.lastLens || codes[0]) && c.dataset.frame === this.view) || this.cards.find((c) => c.dataset.code === (this.lastLens || codes[0]));
    let lensText = '';
    if (detail && shown) lensText = shown.dataset.name + ' · ' + shown.dataset.moment + ' · ' + (this.dataset.tBlocks || '').replace('{pct}', shown.dataset.pct);
    else lensText = codes.map((c) => COLORS[ORDER.indexOf(c)] || c).join(', ');
    this.querySelectorAll('[data-wpb-lens-val]').forEach((el) => { el.textContent = lensText; });

    // price
    const price = (k) => Number(this.byKey[k].dataset.price);
    const original = this.list().reduce((s, k) => s + price(k), 0);
    const pack = this.matchedPack();
    let now = original, was = null;
    if (n >= 2) {
      now = pack ? pack.price : Math.round(original / 2);
      was = pack ? (pack.compare || original) : original;
    }
    const pct = was && was > now ? (was - now) / was : 0;
    const money = (c) => (Number.isFinite(c) ? formatMoney(c, this.currency, this.locale) : '');
    let pctText = '';
    try { pctText = new Intl.NumberFormat(this.locale, { style: 'percent', maximumFractionDigits: 0 }).format(pct); } catch { pctText = Math.round(pct * 100) + '%'; }
    const set = (sel, text) => this.querySelectorAll(sel).forEach((el) => { el.textContent = text; });
    set('[data-wbb-v2-now]', money(now));
    set('[data-wbb-v2-was]', pct > 0 ? money(was) : '');
    set('[data-wbb-v2-save]', pct > 0 ? (this.dataset.tSave || '').replace('{pct}', pctText) : '');
    let pillText = '';
    if (pct > 0) { try { pillText = new Intl.NumberFormat(this.locale, { style: 'percent', maximumFractionDigits: 0, signDisplay: 'always' }).format(-pct); } catch { pillText = '-' + Math.round(pct * 100) + '%'; } }
    set('[data-wpb-pill]', pillText);

    // summary
    set('[data-wbb-v2-selected]', (n === 1 ? this.dataset.tSelOne : this.dataset.tSelMany || '').replace('{n}', String(n)));
    let offToken = '';
    if (pct > 0) { try { offToken = new Intl.NumberFormat(this.locale, { style: 'percent', maximumFractionDigits: 0, signDisplay: 'always' }).format(-pct); } catch { offToken = '-' + Math.round(pct * 100) + '%'; } }
    const nudgeTxt = (this.querySelector('[data-wpb-head]') || {dataset:{}}).dataset.tNudgeOne || '';
    const nudge = n === 1 && !!nudgeTxt;
    this.classList.toggle('is-nudge', nudge);
    const free = n === 1 ? '' : n === 2 ? this.dataset.tFreeOne : n === 4 ? this.dataset.tFreeTwo : n >= 3 ? (offToken || this.dataset.tOff) : '';
    set('[data-wbb-v2-free]', free || '');
    const freeEl = this.querySelector('[data-wbb-v2-free]'); if (freeEl) freeEl.classList.toggle('is-nudge', nudge);
    // pre-launch countdown: only while a pre-order pair is selected
    this.querySelectorAll('[data-wpb-countdown]').forEach((el) => { this.cdEl = el; el.hidden = !this.list().some((k) => this.byKey[k].dataset.preorder === 'true'); });
    this.tickCountdown();
    // shipping line: only when a pre-order pair is in the selection
    const flags = this.list().map((k) => this.byKey[k].dataset.preorder === 'true');
    const anyPre = flags.some(Boolean), allPre = flags.every(Boolean);
    this.liveBlocks(anyPre);
    this.applyButtonText(anyPre);
    // heading: the next pair to pick, with its offer in bold
    {
      const head = this.querySelector('[data-wpb-head]');
      if (head) {
        const d = head.dataset;
        const raw = n < 1 ? d.tDefault : (n === 1 ? d.tP2 : n === 2 ? d.tP3 : n === 3 ? d.tP4 : n === 4 ? d.tP5 : n === 5 ? d.tP6 : d.tPmore) || d.tDefault;
        const esc = (raw || '').replace(/&/g, '&amp;').replace(/</g, '&lt;');
        const html = esc.replace(/\[\[(.+?)\]\]/g, '<strong>$1</strong>');
        if (head.innerHTML !== html) head.innerHTML = html;
        // one line only: shrink the text until it fits the width
        head.style.whiteSpace = 'nowrap'; head.style.fontSize = '';
        let fs = parseFloat(getComputedStyle(head).fontSize) || 24;
        while (head.scrollWidth > head.clientWidth + 1 && fs > 11) { fs -= 0.5; head.style.fontSize = fs + 'px'; }
      }
    }
    const another = this.querySelector('[data-wbb-v2-another]');
    if (another) another.hidden = n >= MAX;
    this.gallerySync();
  }

  // ----- gallery: the photos shown are the ones of the selected product / bundle -----
  targets() {
    const l = this.list();
    const out = [];
    if (l.length >= 2) {
      const ph = this.packHandle[l.join('+')];
      if (ph) out.push(ph);
    }
    const key = l.length === 1 ? l[0] : (this.lastKey && this.q[this.lastKey] ? this.lastKey : l[l.length - 1]);
    if (key && this.byKey[key]) out.push(this.byKey[key].dataset.handle);
    return out.filter(Boolean);
  }
  gallerySync() {
    const t = this.targets();
    if (!t.length || t[0] === this.galleryHandle) return;
    if (t.length > 1 && t[1] === this.galleryHandle && this.galleryCache && this.galleryCache[t[0]] === null) return;
    clearTimeout(this.galleryTimer);
    const token = (this.galleryToken = (this.galleryToken || 0) + 1);
    this.galleryTimer = setTimeout(() => this.loadGallery(t, token), 160);
  }
  async loadGallery(handles, token) {
    this.galleryCache = this.galleryCache || {};
    for (const h of handles) {
      if (h === this.galleryHandle) return;
      let html = this.galleryCache[h];
      if (html === undefined) {
        try {
          const root = window.Shopify?.routes?.root || '/';
          const r = await fetch(root + 'products/' + h);
          if (!r.ok) { this.galleryCache[h] = null; continue; }
          const doc = new DOMParser().parseFromString(await r.text(), 'text/html');
          const g = doc.querySelector('product-gallery');
          html = g ? g.outerHTML : null;
          this.galleryCache[h] = html;
        } catch { continue; }
      }
      if (!html) continue;
      if (token !== this.galleryToken) return; // a newer selection superseded this one
      const current = document.querySelector('product-gallery');
      if (!current) return;
      const tpl = document.createElement('template');
      tpl.innerHTML = html;
      const next = tpl.content.firstElementChild;
      next.removeAttribute('data-wbg-ready');
      const chips = next.querySelector('[data-wbg-chips]');
      if (chips) chips.remove(); // legacy lens chips of the black pages: inert here
      current.replaceWith(next);
      this.galleryHandle = h;
      this.filterLensPhotos(next);
      return;
    }
  }

  // black product pages tag every photo with its lens (data-wb-lens, C = common): show only the selected lenses' photos
  filterLensPhotos(gallery) {
    const carousel = gallery.querySelector('media-carousel');
    if (!carousel) return;
    const slides = Array.from(carousel.children);
    if (!slides.some((s) => s.hasAttribute('data-wb-lens'))) return;
    const codes = new Set(this.list().map((k) => k.split(':')[1]));
    const hidden = [];
    slides.forEach((s, i) => { const l = s.getAttribute('data-wb-lens') || 'C'; if (l !== 'C' && !codes.has(l)) hidden.push(i); });
    const run = () => {
      carousel.dispatchEvent(new CustomEvent('carousel:filter', { detail: { filteredIndexes: hidden } }));
      gallery.querySelectorAll('page-dots').forEach((c) => c.dispatchEvent(new CustomEvent('control:filter', { detail: { filteredIndexes: hidden } })));
    };
    Promise.all([customElements.whenDefined('media-carousel'), customElements.whenDefined('page-dots')]).then(() => requestAnimationFrame(run));
  }

  // ----- cart -----
  async submit(form) {
    if (this.submitting) return;
    this.submitting = true;
    const buttons = Array.from(form.elements).filter((el) => el.type === 'submit');
    buttons.forEach((b) => b.setAttribute('aria-busy', 'true'));
    const sections = ['variant-added'];
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));
    const pack = this.matchedPack();
    let items;
    if (pack) items = [{ id: pack.variantId, quantity: 1 }];
    else {
      items = Object.keys(this.q).map((k) => ({ id: Number(this.byKey[k].dataset.variantId), quantity: this.q[k] })).filter((i) => i.id && i.quantity > 0);
    }
    const root = window.Shopify?.routes?.root || '/';
    try {
      const response = await fetch(root + 'cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: JSON.stringify({ items, sections: sections.join(',') }) });
      buttons.forEach((b) => b.removeAttribute('aria-busy'));
      this.submitting = false;
      const json = await response.json();
      if (response.ok) {
        if (window.themeVariables?.settings?.cartType === 'page' || window.themeVariables?.settings?.pageType === 'cart') { window.location.href = root + 'cart'; return; }
        const cart = await (await fetch(root + 'cart.js')).json();
        cart.sections = json.sections;
        form.dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail: { items: Object.prototype.hasOwnProperty.call(json, 'items') ? json.items : [json], cart } }));
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart } }));
      } else {
        form.dispatchEvent(new CustomEvent('cart:error', { bubbles: true, detail: { error: json.description } }));
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      }
    } catch (err) {
      buttons.forEach((b) => b.removeAttribute('aria-busy'));
      this.submitting = false;
    }
  }
}

if (typeof customElements !== 'undefined' && !customElements.get('warblue-pair-builder')) customElements.define('warblue-pair-builder', WarbluePairBuilder);
